import React, { useRef, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Filters } from '@rocco/ui/filter-sort/components/Filters';
import { FilterTabs } from '@rocco/ui/filter-sort/components/filters-bar/FilterTabs';
import { FilterSeparator } from '@rocco/ui/filter-sort/components/filters-bar/FilterSeparator';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { useRootData } from '~/modules/root';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import { getFilterSettings } from '../util/get-filter-settings';

interface SearchPageFilterTabsProps extends ReturnType<
    typeof useCategoryFilterSort
> {
    /**
     * Optional location object from Remix Router (pathname, search).
     * When provided, will be passed to Filters for auto-close behavior on navigation.
     */
    location?: { pathname: string; search: string } | null;
}

/**
 * Renders the filter bar section, including filter tabs and potentially separator and other filter controls.
 * Adapts layout for mobile and desktop views.
 * Includes horizontal scroll-to-view functionality for active tabs on the search page.
 */
export const SearchPageFilterTabs: React.FC<SearchPageFilterTabsProps> = (
    props,
) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const filterTabs = useMemo(
        () => pageDataFromAtom?.filterTabs ?? [],
        [pageDataFromAtom?.filterTabs],
    );
    const searchIndex = pageDataFromAtom?.searchIndex;
    const { appDisplayMode } = useRootData();
    const isMobile = appDisplayMode === 'mobile';

    // Create refs for tab containers to enable horizontal scroll-to-view functionality
    const desktopTabContainerRef = useRef<HTMLDivElement>(null);
    const mobileTabContainerRef = useRef<HTMLDivElement>(null);

    const filterSettings = getFilterSettings(searchIndex);
    const hasBarFilters = (filterSettings?.config?.barFilters?.length ?? 0) > 0;

    const { location, ...restProps } = props;
    const filterProps = {
        ...restProps,
        displayMode: appDisplayMode,
        location,
        filterButtonVariant: 'primary' as const,
        hideFacetFilters: true,
    };

    if (!pageDataFromAtom) {
        // Render nothing or a placeholder if essential data isn't loaded
        return null;
    }

    return !isMobile ? (
        <div
            className="sticky top-0 z-sticky mt-10 hidden overflow-x-auto bg-background md:mt-0 md:block"
            ref={desktopTabContainerRef}
            style={{ scrollBehavior: 'smooth' }}
        >
            <div className="flex h-20 items-center pl-6 md:pl-0">
                <Filters {...filterProps}>
                    {filterTabs.length > 0 && (
                        <>
                            <FilterTabs
                                tabs={filterTabs}
                                displayMode={appDisplayMode}
                                tabContainerRef={
                                    desktopTabContainerRef as React.RefObject<HTMLDivElement>
                                }
                            />
                            {hasBarFilters && <FilterSeparator />}
                        </>
                    )}
                </Filters>
            </div>
        </div>
    ) : (
        <div
            className="sticky top-0 z-sticky scrollbar-hide overflow-x-auto bg-background py-7 pr-0 pl-6 md:hidden"
            ref={mobileTabContainerRef}
            style={{ scrollBehavior: 'smooth' }}
        >
            <Filters {...filterProps}>
                {filterTabs.length > 0 && (
                    <FilterTabs
                        tabs={filterTabs}
                        displayMode={appDisplayMode}
                        tabContainerRef={
                            mobileTabContainerRef as React.RefObject<HTMLDivElement>
                        }
                    />
                )}
            </Filters>
        </div>
    );
};
